import {
  useTriggerLoad,
  useTriggerLoadWithInput,
} from "@redotech/react-util/load";
import { PickupLocation, ReturnZod } from "@redotech/redo-model/return";
import { PickupPayer } from "@redotech/redo-model/return-flow";
import { useContext, useState } from "react";
import { RpcClientContext } from "../../contexts/rpc";
import { getValidDate } from "../../shipment-method/util";
import {
  MAX_DAYS,
  MIN_DAYS,
  MIN_DAYS_FOR_MANUAL_REVIEW,
} from "../usePackagePickup/use-package-pickup";

export const usePickupModals = ({
  returnData,
}: {
  returnData: ReturnZod | undefined;
}) => {
  const client = useContext(RpcClientContext);

  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const today = Temporal.Now.plainDateISO();
  const isManualReview = returnData?.markedForManualReview ?? false;
  const minDays = isManualReview ? MIN_DAYS_FOR_MANUAL_REVIEW : MIN_DAYS;
  const minDate = getValidDate(today, minDays);
  const maxDate = getValidDate(today, MAX_DAYS);

  const [reschedulePickupLoad, handleReschedulePickup] =
    useTriggerLoadWithInput(
      async ({
        pickupDate,
        location,
      }: {
        pickupDate: Temporal.PlainDate | undefined;
        location: PickupLocation | undefined;
      }) => {
        if (!client || !returnData?._id) {
          return;
        }
        await client.reschedulePickup({
          returnId: returnData._id,
          pickupDate: pickupDate?.toJSON(),
          location,
        });
        window.location.reload();
      },
    );

  const [cancelPickupLoad, handleCancelPickup] = useTriggerLoad(async () => {
    if (!client || !returnData?._id) {
      return;
    }
    await client.cancelPickup({ returnId: returnData._id });
    window.location.reload();
  });

  const reschedulePending = reschedulePickupLoad.pending;
  const cancelPending = cancelPickupLoad.pending;
  const hasRescheduleError: boolean = reschedulePickupLoad.error !== undefined;
  const hasCancelError: boolean = cancelPickupLoad.error !== undefined;

  return {
    pickupFee: returnData?.pickupMetrics?.rate ?? 0,
    pickupInfo: {
      pickupDate: returnData?.pickup?.pickupDate
        ? Temporal.PlainDate.from(returnData.pickup.pickupDate)
        : minDate,
      location: returnData?.pickup?.pickupLocation?.packageLocation,
    },
    isMerchantCoveredPickup:
      returnData?.pickup?.pickupPayer === PickupPayer.MERCHANT,
    rescheduleModalOpen,
    setRescheduleModalOpen,
    cancelModalOpen,
    setCancelModalOpen,
    minDate,
    maxDate,
    reschedulePending,
    cancelPending,
    handleReschedulePickup,
    handleCancelPickup,
    hasRescheduleError,
    hasCancelError,
  };
};
